import DownloadOutlined from "@ant-design/icons/lib/icons/DownloadOutlined";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Radio,
  Select,
  Table,
  Tooltip,
} from "antd";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  AccountBean,
  DspBean,
  KeyFilterClause,
  PublisherBean,
  PublisherReportKeyColumnDescription,
  PublisherReportValueColumnDescription,
  ScheduledReportRequestObject,
  ScheduledReportRequestReportRequestParametersObjectObject,
} from "src/api/api";
import { formDataLoader, FormDataType } from "src/util/FormDataLoader";
import reportApi from "./ReportApi";
import scheduledReportApi from "../scheduledReport/ScheduledReportApi";
import { useUser } from "../../../context/UserContext";
import {
  beautifyReportData,
  isAdmin,
  isReporting,
  lowerize,
} from "../../../util/Util";
import moment from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import accountApi from "../account/AccountApi";

moment.extend(utc);
moment.extend(timezonePlugin);
import "./style.css";
import { calculateColumnsWidth } from "../rawReports/dynamicColumnsHelper";
import {
  buildFilteredReportTotalRow,
  getReportColumnLabel,
  getReportFilterableKeys,
  headerDescription,
  sortReportRows,
} from "./ReportUtil";
import ColumnFilterChecklist, { renderColumnFilterIcon } from "../ui/ColumnFilterChecklist";
import ColumnEnum = PublisherReportKeyColumnDescription.ColumnEnum;
import Papa from "papaparse";


const { RangePicker } = DatePicker;
const dateFormat = "YYYY-MM-DD";
const FormItem = Form.Item;
const Option = Select.Option;
const PublisherKeyColumns = PublisherReportKeyColumnDescription.ColumnEnum;
const PublisherValueColumns = PublisherReportValueColumnDescription.ColumnEnum;

// Extend generated ColumnEnum to include API-supported columns not yet in generated types
type ExtendedPublisherKeyColumn = PublisherReportKeyColumnDescription.ColumnEnum | 'DealType';
type ExtendedKeyColumnDescription = Omit<PublisherReportKeyColumnDescription, 'column'> & {
  column?: ExtendedPublisherKeyColumn;
};
const formInfoItems: FormDataType[] = [
  FormDataType.Publishers,
  FormDataType.DspList,
  FormDataType.PublisherReportColumns,
];

const MemoizedReportTable = React.memo<{
  tableWidth: number;
  tableColumns: any[];
  filteredSource: any[];
  header: string[] | undefined;
  onTableChange?: (sorter: any) => void;
}>(({ tableWidth, tableColumns, filteredSource, header, onTableChange }) => {
  return (
    <Table
      pagination={false}
      scroll={{
        x: tableWidth,
        y: 700,
      }}
      columns={tableColumns}
      dataSource={filteredSource}
      onChange={(_, __, sorter) => onTableChange?.(sorter)}
    />
  );
});

export const PublisherReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(undefined);
  const [form] = Form.useForm();

  const [scheduled, setScheduled] = useState<boolean>(false);
  const [blobCsv, setBlobCsv] = useState<any>();
  const [header, setHeader] = useState<string[]>();
  const [data, setData] = useState<string[][]>();
  const [totals, setTotals] = useState<{ [key: string]: number }>();
  const [sortConfig, setSortConfig] = useState<{ key: string | null; order: 'ascend' | 'descend' | null }>({ key: null, order: null });
  const [hoveredHeader, setHoveredHeader] = useState<string | null>(null);
  const [hoveredSortCell, setHoveredSortCell] = useState<string | null>(null);
  const [hoveredFilter, setHoveredFilter] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);

  const [inputValue, setInputValue] = useState<string>("");
  const [debouncedCountrySearch, setDebouncedCountrySearch] = useState<string>("");
  const [formatSizeInput, setFormatSizeInput] = useState<string>("");
  const [debouncedFormatSizeSearch, setDebouncedFormatSizeSearch] = useState<string>("");

  const hasCountryColumn = useMemo(() => {
    return header?.some((h) => (h ?? "").toLowerCase() === "country") ?? false;
  }, [header]);

  const hasSizeColumn = useMemo(() => {
    return header?.some((h) => (h ?? "").toLowerCase() === "size" || (h ?? "").toLowerCase() === "format size") ?? false;
  }, [header]);

  const [apiColumns, setApiColumns] = useState<string[]>([]);
  const [apiGroupBy, setApiGroupBy] = useState<string[]>([]);
  const [reportGroupBy, setReportGroupBy] = useState<string[]>([]);

  const { user } = useUser();
  const admin = isAdmin(user);
  const reporting = isReporting(user);



    const getKeyColumns = (cols: string[], formValues: any) => {

        const res: ExtendedKeyColumnDescription[] = []
        res.push({ column: PublisherKeyColumns.Publisher })

        if (formValues.publishers && formValues.publishers.length > 0) {
            if (formValues.publishers.length > 1) {

                res.push({
                    column: PublisherKeyColumns.PublisherId,
                    where: {
                        type: KeyFilterClause.TypeEnum.In,
                        value: formValues.publishers
                    },
                    onlyFilter: true
                })
            } else if (formValues.publishers.length === 1) {
                res.push({
                    column: PublisherKeyColumns.PublisherId,
                    where: {
                        type: KeyFilterClause.TypeEnum.Equals,
                        value: formValues.publishers
                    },
                    onlyFilter: true
                })
            }
        }

        let isDspSelected: boolean = false
        if (cols?.findIndex(col => col === PublisherKeyColumns.Dsp.toString()) !== -1) {
            res.push({
                column: PublisherKeyColumns.Dsp,
            })
            isDspSelected = true
        }

        if (formValues.dsps && formValues.dsps.length > 0) {
            res.push({
                column: PublisherKeyColumns.DspId,
                where: {
                    type: KeyFilterClause.TypeEnum.In,
                    value: formValues.dsps
                },
                onlyFilter: true
            })
        } else {
            res.push({
                column: PublisherKeyColumns.DspId,
                where: {
                    type: isDspSelected ? KeyFilterClause.TypeEnum.NotEquals : KeyFilterClause.TypeEnum.Equals,
                    value: 0
                },
                onlyFilter: true
            })
        }

        let dateCol: PublisherReportKeyColumnDescription
        dateCol = {
            column: formValues.groupingPeriod,
            where: {
                type: KeyFilterClause.TypeEnum.Between,
                value: formValues.dates.map(d => d.format(dateFormat))
            }
        }
        if (cols?.findIndex(col => col === PublisherKeyColumns.Date.toString()) === -1) {
            dateCol.onlyFilter = true
        }
        res.push(dateCol)

        // Columns with special where clauses
        const specialWhereColumns: Record<string, any> = {
            [PublisherKeyColumns.Size.toString()]: {
                type: KeyFilterClause.TypeEnum.NotEquals,
                value: ''
            },
            [PublisherKeyColumns.MediaType.toString()]: {
                type: KeyFilterClause.TypeEnum.NotEquals,
                value: ''
            },
        };

        // Columns already handled above
        const handledKeyColumns = new Set([
            PublisherKeyColumns.Publisher.toString(),
            PublisherKeyColumns.PublisherId.toString(),
            PublisherKeyColumns.Dsp.toString(),
            PublisherKeyColumns.DspId.toString(),
            PublisherKeyColumns.Date.toString(),
            PublisherKeyColumns.FourHourly.toString(),
            PublisherKeyColumns.Daily.toString(),
            PublisherKeyColumns.Weekly.toString(),
            PublisherKeyColumns.Monthly.toString(),
            PublisherKeyColumns.Yearly.toString(),
        ]);

        const groupBySet = new Set(
            apiGroupBy.length > 0
                ? apiGroupBy
                : Object.keys(Object.assign({}, PublisherKeyColumns))
        );

        cols?.filter(col => groupBySet.has(col) && !handledKeyColumns.has(col))
            .forEach(col => {
                if (specialWhereColumns[col]) {
                    res.push({ column: col as any, where: specialWhereColumns[col] });
                } else {
                    res.push({ column: col as any });
                }
            });

        return res as unknown as PublisherReportKeyColumnDescription[]
    }

  const getValueColumns = (cols: string[], formValues: any) => {
    if (!cols) return [];
    const valueColumnSet = new Set(
      apiColumns.length > 0
        ? apiColumns
        : Object.keys(Object.assign({}, PublisherValueColumns))
    );
    return cols
      .filter((col) => valueColumnSet.has(col))
      .map((col) => ({ column: col as any }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setBlobCsv(undefined);
    setData(undefined);
    setTotals(undefined);
    setSortConfig({ key: null, order: null });
    setColumnFilters({});
    setReportGroupBy([]);
    setOpenFilterKey(null);
    setInputValue("");
    setDebouncedCountrySearch("");
    setFormatSizeInput("");
    setDebouncedFormatSizeSearch("");

    const values = await form.validateFields();
    if (user?.role == AccountBean.RoleEnum.Publisher) {
      values.publishers = [user.publisherId];
    }
    const cols = values.columns as string[];
    const groups = values.groups as string[];

    const allCols = ([] as string[]).concat(cols, groups);

    const keyCols = getKeyColumns(allCols, values);
    const valueCols = getValueColumns(allCols, values);

    if (scheduled) {
      await scheduledReportApi
        .createPublisher({
          requestParameters: {
            keyColumns: keyCols,
            valueColumns: valueCols,
            type: ScheduledReportRequestObject.ReportTypeEnum.ByPublisher,
          },
          name: form.getFieldValue("name"),
          reportType: ScheduledReportRequestObject.ReportTypeEnum.ByPublisher,
          nextReport: form
            .getFieldValue("nextReport")
            .format("YYYY-MM-DD HH:mm"),
          frequency: form.getFieldValue("frequency"),
        })
        .then((resp) => {
          message.success('Scheduled report created successfully.', 4);
          return resp.data;
        })
        .catch((err) => {
          onRejected(err);
        })
        .finally(() => setLoading(false));
      return;
    }

    let uuid: string | undefined;
    try {
      uuid = await reportApi
        .createPublisher({
          keyColumns: keyCols,
          valueColumns: valueCols,
        })
        .then((resp) => resp.data);
    } catch (err) {
      onRejected(err);
      setLoading(false);
      return;
    }
    if (!uuid) {
      setLoading(false);
      return;
    }
    let interval = setInterval(async () => {
      try {
        const response = await reportApi.get(uuid ?? "");
        clearInterval(interval);
        const blob = await response.blob();
        const text = await blob.text();
    
        if (text.length < 1) return;
    
        const parsed = Papa.parse<string[]>(text, {
          skipEmptyLines: true,
           delimiter: ",", 
        });
    
const rows = parsed?.data ?? [];
    
        // if (rows.length < 2) return;
    
        setHeader(rows[0]);
        setReportGroupBy(groups ?? []);
        setSortConfig({ key: rows[0]?.[0]?.toLowerCase() ?? null, order: 'ascend' });
        setData(rows.slice(1));
        setBlobCsv(blob as any);
        setLoading(false);
    
        if (rows.length > 1) {
          const reportInfo = await reportApi.info(uuid ?? "");
          const reportBean = reportInfo.data;
          setTotals(reportBean?.totalValues);
        }
      } catch (response) {
        if (typeof response === "object") {
          if (response.status !== 425) {
            setLoading(false);
            clearInterval(interval);
          }
        }
      }
    }, 500);
  };

  const [info, setInfo] = useState({
    publishers: [],
    dspList: [],
  });
  const [userTimezone, setUserTimezone] = useState<string>("");

  const onRejected = (err) => {
    setError(err.error);
  };

  const loadData = async () => {
    try {
      if (admin || reporting) {
        let formInfo = await formDataLoader(...formInfoItems);
        setInfo(formInfo as any);
        setApiColumns(formInfo.publisherReportColumns?.columns ?? []);
        setApiGroupBy(formInfo.publisherReportColumns?.groupBy ?? []);
      }
      const tz = user?.id
        ? (await accountApi.view(user.id))?.data?.timezoneId || (moment as any).tz.guess()
        : (moment as any).tz.guess();
      setUserTimezone(tz);
      const nowInUserTz = (moment() as any).tz(tz);
      const offsetDiff = nowInUserTz.utcOffset() - moment().utcOffset();
      form.setFieldsValue({ nextReport: moment().add(offsetDiff, "minute") });
    } catch (e) {
      onRejected(e);
    }
  };

  useEffect(() => {
    loadData();
  }, [user?.id]);

  useEffect(() => {
    if (scheduled && userTimezone) {
      const nowInUserTz = (moment() as any).tz(userTimezone);
      const offsetDiff = nowInUserTz.utcOffset() - moment().utcOffset();
      form.setFieldsValue({ nextReport: moment().add(offsetDiff, "minute") });
    }
  }, [scheduled]);

  useEffect(() => {
    beautifyReportData(header, data);
  }, [data]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCountrySearch(inputValue);
    }, 1000);

    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFormatSizeSearch(formatSizeInput);
    }, 1000);

    return () => clearTimeout(timer);
  }, [formatSizeInput]);

  const headerFunc = (headerToProcess?: any[]) => {
    const mobileAppIdIndex = headerToProcess?.findIndex(h => h.toLowerCase() === 'mobileappid') ?? -1;

    return headerToProcess?.map((h, index) => {
      const lowerKey = h.toLowerCase();
      const isDomainOrMobileApp = lowerKey === 'domain' || lowerKey === 'mobileappid';
      const isDomain = lowerKey === 'domain';
      const isBeforeMobileAppId = mobileAppIdIndex !== -1 && index < mobileAppIdIndex;

      const description = headerDescription.find((hd) => hd.h === h)?.d;

      const sortHint = sortConfig.key === lowerKey
        ? (sortConfig.order === 'ascend' ? 'Click to sort descending' : 'Click to sort ascending')
        : 'Click to sort ascending';
      const suppressSortTooltip =
        hoveredHeader === lowerKey ||
        hoveredFilter === lowerKey ||
        openFilterKey === lowerKey;

      return {
        title: (
          <Tooltip
            title={description ?? undefined}
            placement="topLeft"
            open={hoveredHeader === lowerKey}
          >
            <span
              onMouseEnter={() => setHoveredHeader(lowerKey)}
              onMouseLeave={() => setHoveredHeader(null)}
              onClick={(e) => e.stopPropagation()}
            >
              {h}
            </span>
          </Tooltip>
        ),
        dataIndex: lowerKey,
        key: lowerKey,
        width: isDomain ? 300 : (isBeforeMobileAppId ? 150 : undefined),
        ellipsis: isDomainOrMobileApp ? {
          showTitle: false,
        } : false,
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: {
          title: suppressSortTooltip ? null : sortHint,
          align: { offset: [0, -25] },
          open: hoveredSortCell === lowerKey && !suppressSortTooltip,
          mouseLeaveDelay: 0,
          overlayStyle: { pointerEvents: 'none' },
        },
        onHeaderCell: () => ({
          onMouseEnter: () => setHoveredSortCell(lowerKey),
          onMouseLeave: () => setHoveredSortCell(null),
        }),
        render: isDomainOrMobileApp ? (text: string) => (
          <Tooltip title={text} placement="topLeft">
            <span style={{
              display: 'block',
              wordBreak: 'break-all',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {text}
            </span>
          </Tooltip>
        ) : undefined,
      };
    });
  };

  const sourceFunc = useCallback(
  (
    dataToProcess?: any[],
    headerToProcess?: any[]
  ) => {
    const cleanValue = (key: string, value: string) => {
      const lowerKey = key?.toLowerCase() ?? "";

      if (
        typeof value === "string" &&
        (
          (
            (lowerKey.includes("date") || lowerKey.includes("time")) &&
            /^\d{1,4},\d{3}-\d{2}-\d{2}/.test(value)
          ) ||
          lowerKey.includes("seller account id")
        )
      ) {
        return value.replace(/,/g, "");
      }

      return value;
    };

    const source =
      dataToProcess?.map((row) => {
        const isTotalsRow = row[0] === "";
        const obj = headerToProcess?.reduce((o, key, index) => {
          const lowerKey = key?.toLowerCase() ?? "";
          const value =
            index === 0 && isTotalsRow
              ? "Total"
              : cleanValue(lowerKey, row[index]);
          return { ...o, [lowerKey]: value };
        }, {}) ?? {};
        return isTotalsRow ? { ...obj, _isTotalRow: true } : obj;
      }) ?? [];

    return source;
  },
  []
);



  const applyFilters = useCallback((source: any[], headerToProcess?: any[], countrySearchTerm?: string, formatSizeSearchTerm?: string) => {
    let filtered = source ?? [];
    if (!filtered.length) return filtered;

    if (countrySearchTerm?.trim()) {
      const hasCountry = headerToProcess?.some(
        (h) => (h ?? "").toLowerCase() === "country"
      );
      if (hasCountry) {
        const search = countrySearchTerm.toLowerCase();
        filtered = filtered.filter((row: any) => {
          const value = (row?.["country"] ?? "").toString().toLowerCase();
          return value.includes(search);
        });
      }
    }

    if (formatSizeSearchTerm?.trim()) {
      const hasSize = headerToProcess?.some(
        (h) => (h ?? "").toLowerCase() === "size" || (h ?? "").toLowerCase() === "format size"
      );
      if (hasSize) {
        const search = formatSizeSearchTerm.toLowerCase();
        filtered = filtered.filter((row: any) => {
          const value = (row?.["size"] ?? row?.["format size"] ?? "").toString().toLowerCase();
          return value.includes(search);
        });
      }
    }

    return filtered;
  }, []);

  const download = () => {
    const source = sourceFunc(data, header);
    const filteredSource = applyFilters(source ?? [], header, debouncedCountrySearch, debouncedFormatSizeSearch);

    if ((debouncedCountrySearch?.trim() || debouncedFormatSizeSearch?.trim()) && filteredSource.length > 0 && header) {
      const csvRows: string[][] = [];

      csvRows.push(header);

      filteredSource.forEach((row) => {
        const rowData = header.map((h) => {
          const value = row[h.toLowerCase()];
          return value != null ? value.toString() : "";
        });
        csvRows.push(rowData);
      });

      const csvContent = Papa.unparse(csvRows);

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "report.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } else {
      var link = document.createElement("a");
      if (link.download !== undefined) {
        var url = URL.createObjectURL(blobCsv);
        link.setAttribute("href", url);
        link.setAttribute("download", "report.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const handleSelectAllColumns = (value: any) => {
    if (value && value.length && value.includes("__all")) {
      form.setFieldsValue({ dsps: [] });
      form.setFieldsValue({
        groups: form
          .getFieldValue("groups")
          ?.filter(
            (item) =>
              item != PublisherKeyColumns.Dsp &&
              item != PublisherKeyColumns.Size &&
              item != PublisherKeyColumns.MediaType
          ),
      });
      return [
        ...(apiColumns.length > 0 ? apiColumns : Object.keys(
          Object.assign({}, PublisherReportValueColumnDescription.ColumnEnum)
        )),
      ];
    }
    return value;
  };

  const handleSelectAllGroups = (value: any) => {
    if (value && value.length && value.includes("__all")) {
      form.setFieldsValue({
        columns: form
          .getFieldValue("columns")
          ?.filter(
            (item) =>
              item != PublisherValueColumns.AvgLowestBidPrice &&
              item != PublisherValueColumns.AvgMedianBidPrice &&
              item != PublisherValueColumns.AvgHighestBidPrice &&
              item != PublisherValueColumns.Requests
          ),
      });

      return [
        ...(apiGroupBy.length > 0 ? apiGroupBy : Object.keys(
          Object.assign({}, PublisherReportKeyColumnDescription.ColumnEnum)
        ).filter(
          (key) =>
            ![
              PublisherReportKeyColumnDescription.ColumnEnum.PublisherId.toString(),
              PublisherReportKeyColumnDescription.ColumnEnum.DspId.toString(),
              PublisherReportKeyColumnDescription.ColumnEnum.Publisher.toString(),
              PublisherReportKeyColumnDescription.ColumnEnum.FourHourly.toString(),
              PublisherReportKeyColumnDescription.ColumnEnum.Daily.toString(),
              PublisherReportKeyColumnDescription.ColumnEnum.Weekly.toString(),
              PublisherReportKeyColumnDescription.ColumnEnum.Monthly.toString(),
              PublisherReportKeyColumnDescription.ColumnEnum.Yearly.toString(),
            ].includes(key)
        )),
      ];
    } else if (
      value &&
      (!value.length || !value.includes(PublisherKeyColumns.Dsp))
    ) {
      form.setFieldsValue({ dsps: [] });
    }
    return value;
  };

  const handleDspSelect = (value: any) => {
    if (value && value.length) {
      let groupsField: [any] = form.getFieldValue("groups");
      if (!groupsField || !groupsField.length) {
        groupsField = ["Dsp"];
        form.setFieldsValue({ groups: groupsField });
      } else if (!groupsField.includes("Dsp")) {
        groupsField.push("Dsp");
        form.setFieldsValue({ groups: groupsField });
      }
      handleGroupsSelect(groupsField);
    }
    return value;
  };

const handleColumnsSelect = (value: any) => {
  if (
    value.find(
      (item) =>
        item === PublisherValueColumns.AvgLowestBidPrice ||
        item === PublisherValueColumns.AvgMedianBidPrice ||
        item === PublisherValueColumns.AvgHighestBidPrice
    )
  ) {
    form.setFieldsValue({ dsps: [] });
    form.setFieldsValue({
      groups: form
        .getFieldValue("groups")
        ?.filter((item) => item != PublisherKeyColumns.Dsp),
    });
  }
  if (value.includes(PublisherValueColumns.Requests)) {
    form.setFieldsValue({
      groups: form
        .getFieldValue("groups")
        ?.filter(
          (item) =>
            item != PublisherKeyColumns.Size &&
            item != PublisherKeyColumns.MediaType
        ),
    });
  }
};

const handleGroupsSelect = (value: any) => {
  if (value.includes(PublisherKeyColumns.Dsp)) {
    form.setFieldsValue({
      columns: form
        .getFieldValue("columns")
        ?.filter(
          (item) =>
            item != PublisherValueColumns.AvgLowestBidPrice &&
            item != PublisherValueColumns.AvgMedianBidPrice &&
            item != PublisherValueColumns.AvgHighestBidPrice
        ),
    });
  }
  if (
    value.find(
      (item) =>
        item === PublisherKeyColumns.Size ||
        item === PublisherKeyColumns.MediaType 
    )
  ) {
    form.setFieldsValue({
      columns: form
        .getFieldValue("columns")
        ?.filter((item) => item != PublisherValueColumns.Requests),
    });
  }
};

const columnsOptionDescriptionFunc = (key: any) => {
  if (key == PublisherKeyColumns.Dsp) {
    return (
      "You can select " +
      PublisherKeyColumns.Dsp +
      " only without " +
      PublisherValueColumns.AvgLowestBidPrice +
      ", " +
      PublisherValueColumns.AvgMedianBidPrice +
      " and " +
      PublisherValueColumns.AvgHighestBidPrice
    );
  }
  if (
    key == PublisherKeyColumns.Size ||
    key == PublisherKeyColumns.MediaType
  ) {
    return (
      "You can select " +
      PublisherKeyColumns.Size +
      " only without " +
      PublisherValueColumns.Requests
    );
  }
  if (key == PublisherValueColumns.Requests) {
    return (
      "You can select " +
      PublisherValueColumns.Requests +
      " only without " +
      PublisherKeyColumns.Size +
      ", " +
      PublisherKeyColumns.MediaType +
      " and "
    );
  }
  if (key == PublisherValueColumns.AvgLowestBidPrice) {
    return (
      "You can select " +
      PublisherValueColumns.AvgLowestBidPrice +
      " only without " +
      PublisherKeyColumns.Dsp
    );
  }
  if (key == PublisherValueColumns.AvgMedianBidPrice) {
    return (
      "You can select " +
      PublisherValueColumns.AvgMedianBidPrice +
      " only without " +
      PublisherKeyColumns.Dsp
    );
  }
  if (key == PublisherValueColumns.AvgHighestBidPrice) {
    return (
      "You can select " +
      PublisherValueColumns.AvgHighestBidPrice +
      " only without " +
      PublisherKeyColumns.Dsp
    );
  }
  return key;
};

  const processedSource = useMemo(() => {
    return sourceFunc(data, header);
  }, [data, header, sourceFunc]);

  const filteredSource = useMemo(() => {
    return applyFilters(processedSource ?? [], header, debouncedCountrySearch, debouncedFormatSizeSearch);
  }, [processedSource, header, debouncedCountrySearch, debouncedFormatSizeSearch, applyFilters]);

  const valueColumnNames = useMemo(
    () =>
      apiColumns.length > 0 ? apiColumns : Object.keys(PublisherValueColumns),
    [apiColumns],
  );

  const FILTERABLE_KEYS = useMemo(
    () => getReportFilterableKeys(header, reportGroupBy),
    [header, reportGroupBy],
  );

  const columnFilteredSource = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, vals]) => vals.length > 0);
    if (activeFilters.length === 0) return filteredSource;

    const filteredRows = filteredSource
      .filter((r: any) => !r._isTotalRow)
      .filter((r: any) => activeFilters.every(([k, vals]) => vals.includes(String(r[k] ?? ''))));

    if (!header || filteredRows.length === 0) {
      return filteredRows;
    }

    const totalRow = buildFilteredReportTotalRow(
      header,
      filteredRows,
      valueColumnNames,
    );

    return [...filteredRows, totalRow];
  }, [filteredSource, columnFilters, header, valueColumnNames]);

  const filterOptionsByKey = useMemo(() => {
    const map: Record<string, string[]> = {};
    FILTERABLE_KEYS.forEach((k) => {
      map[k] = Array.from(
        new Set(
          filteredSource
            .filter((r: any) => !r._isTotalRow)
            .map((r: any) => String(r[k] ?? ''))
            .filter((v: string) => v && v !== 'undefined')
        )
      ).sort();
    });
    return map;
  }, [filteredSource, FILTERABLE_KEYS]);

  const tableColumns = useMemo(() => {
    const cols = calculateColumnsWidth(
      headerFunc(header) ?? [],
      columnFilteredSource ?? [],
      2000
    ).columns;
    return cols.map((col: any) => {
      const key = col.dataIndex as string;
      const enriched: any = {
        ...col,
        sortOrder: sortConfig.key === key ? sortConfig.order : null,
      };
      if (FILTERABLE_KEYS.has(key)) {
        const options = filterOptionsByKey[key] ?? [];
        const selected = columnFilters[key] ?? [];
        const label = getReportColumnLabel(header, key);
        enriched.filterDropdown = ({ confirm }: { confirm: () => void }) => (
          <ColumnFilterChecklist
            options={options}
            selectedValues={selected}
            searchPlaceholder={`Search ${label}...`}
            onApply={(values) =>
              setColumnFilters((prev) => ({ ...prev, [key]: values }))
            }
            onClose={confirm}
          />
        );
        enriched.filterIcon = renderColumnFilterIcon(
          label,
          selected.length > 0,
          (open) => setHoveredFilter(open ? key : null),
        );
        enriched.filterDropdownOpen = openFilterKey === key;
        enriched.onFilterDropdownOpenChange = (visible: boolean) => {
          setOpenFilterKey(visible ? key : null);
          if (visible) {
            setHoveredSortCell(null);
            setHoveredFilter(null);
          }
        };
      }
      return enriched;
    });
  }, [header, columnFilteredSource, sortConfig, columnFilters, filterOptionsByKey, openFilterKey, FILTERABLE_KEYS, hoveredHeader, hoveredSortCell, hoveredFilter]);

  const sortedSource = useMemo(
    () => sortReportRows(columnFilteredSource, sortConfig.key, sortConfig.order),
    [columnFilteredSource, sortConfig],
  );

  const tableWidth = useMemo(() => {
    return calculateColumnsWidth(
      headerFunc(header) ?? [],
      columnFilteredSource ?? [],
      2000
    ).tableWidth;
  }, [header, columnFilteredSource]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const handleFormatSizeInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormatSizeInput(e.target.value);
  }, []);

  const handleTableChange = useCallback((sorter: any) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    setSortConfig(prev => {
      const clickedKey = (s?.columnKey ?? s?.field ?? prev.key) as string | null;
      if (!clickedKey) return prev;
      return { key: clickedKey, order: s?.order ?? 'ascend' };
    });
  }, []);

  return (
    <div>
      {loading}
      {error && (
        <div>
          <Alert message={error} type="error" />
          <br />
        </div>
      )}
      <Form
        style={{
          maxWidth: "1000px",
        }}
        onFinish={handleSubmit}
        form={form}
        labelCol={{ span: 3 }}
        wrapperCol={{ span: 21 }}
      >
        <FormItem
          label="Period"
          tooltip="Select dates of the report and a grouping period"
          style={{ marginBottom: 0 }}
        >
          <FormItem
            name="dates"
            rules={[
              {
                required: true,
                message: "Please choose dates",
              },
            ]}
            style={{ display: "inline-block" }}
          >
            <RangePicker format="YYYY-MM-DD" />
          </FormItem>
          <span
            style={{
              display: "inline-block",
              lineHeight: "32px",
              textAlign: "left",
              margin: "0 10px 0 20px",
            }}
          >
            Group by:
          </span>

          <FormItem
            name="groupingPeriod"
            initialValue={ColumnEnum.Date}
            style={{ display: "inline-block" }}
          >
            <Radio.Group
              id={"radioButtonsWithoutLines"}
              buttonStyle="solid"
              defaultValue={ColumnEnum.Date}
              size={"middle"}
              style={{ margin: "0px 0px 0px 0px" }}
            >
              <Radio.Button
                id={"radioButtonsWithoutLines"}
                style={{ padding: "0 5px" }}
                key={ColumnEnum.Date}
                value={ColumnEnum.Date}
              >
                1Hr
              </Radio.Button>
              <Radio.Button
                id={"radioButtonsWithoutLines"}
                style={{ padding: "0 5px" }}
                key={ColumnEnum.FourHourly}
                value={ColumnEnum.FourHourly}
              >
                4Hr
              </Radio.Button>
              <Radio.Button
                id={"radioButtonsWithoutLines"}
                style={{ padding: "0 5px" }}
                key={ColumnEnum.Daily}
                value={ColumnEnum.Daily}
              >
                Daily
              </Radio.Button>
              <Radio.Button
                id={"radioButtonsWithoutLines"}
                style={{ padding: "0 5px" }}
                key={ColumnEnum.Weekly}
                value={ColumnEnum.Weekly}
              >
                Weekly
              </Radio.Button>
              <Radio.Button
                id={"radioButtonsWithoutLines"}
                style={{ padding: "0 5px" }}
                key={ColumnEnum.Monthly}
                value={ColumnEnum.Monthly}
              >
                Monthly
              </Radio.Button>
              <Radio.Button
                id={"radioButtonsWithoutLines"}
                style={{ padding: "0 5px" }}
                key={ColumnEnum.Yearly}
                value={ColumnEnum.Yearly}
              >
                Yearly
              </Radio.Button>
            </Radio.Group>
          </FormItem>
        </FormItem>

        {(admin || reporting) && (
          <FormItem
            name="publishers"
            label="Publishers"
            tooltip="Select publishers to generate report with. If none chosen, all publishers will be selected"
          >
            <Select
              showSearch
              allowClear
              mode="multiple"
              placeholder="Select publishers"
              listHeight={200}
              optionFilterProp="children"
              dropdownAlign={{
                points: ['tl', 'bl'],
                offset: [0, 4],   
                overflow: {
                  adjustX: false,
                  adjustY: true, 
                },
              }}
            >
              {info.publishers.map((item: PublisherBean) => (
                <Option key={item.id} value={item.id as number}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </FormItem>
        )}

        {(admin || reporting) && (
          <FormItem
            name="dsps"
            label="Dsps"
            tooltip="Select dsps to generate report with. If none chosen, all dsps will be selected"
          >
            <Select
              showSearch
              allowClear
              mode="multiple"
              placeholder="Select dsps"
              listHeight={200}
              onChange={handleDspSelect}
              optionFilterProp="children"
              dropdownAlign={{
                points: ['tl', 'bl'],
                offset: [0, 4],   
                overflow: {
                  adjustX: false,
                  adjustY: true, 
                },
              }}
            >
              <Option key={-1} value={-1}>
                House Bidder
              </Option>
              {info.dspList.map((item: DspBean) => (
                <Option key={item.id} value={item.id as number}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </FormItem>
        )}
        <FormItem
          name="columns"
          label="Columns"
          tooltip="Select columns to generate report with"
          getValueFromEvent={handleSelectAllColumns}
          rules={[
            {
              required: scheduled,
              message: "Please select columns",
            },
          ]}
        >
          <Select
            showSearch
            allowClear
            mode="multiple"
            placeholder="Select columns"
            listHeight={200}
            onChange={handleColumnsSelect}
            dropdownAlign={{
              points: ['tl', 'bl'],
              offset: [0, 4],   
              overflow: {
                adjustX: false,
                adjustY: true, 
              },
            }}
          >
            {<Option value={"__all"}>{"Select all columns"}</Option>}
            {(apiColumns.length > 0 ? apiColumns : Object.keys(
              Object.assign(
                {},
                PublisherReportValueColumnDescription.ColumnEnum
              )
            )).map((key) => (
              <Option
                title={columnsOptionDescriptionFunc(key)}
                key={key}
                value={key}
              >
                {key}
              </Option>
            ))}
          </Select>
        </FormItem>

        <FormItem
          name="groups"
          label="Group by"
          tooltip="Select grouping values"
          getValueFromEvent={handleSelectAllGroups}
        >
          <Select
            showSearch
            allowClear
            mode="multiple"
            placeholder="Select grouping"
            listHeight={200}
            onChange={handleGroupsSelect}
            dropdownAlign={{
              points: ['tl', 'bl'],
              offset: [0, 4],   
              overflow: {
                adjustX: false,
                adjustY: true, 
              },
            }}
          >
            {<Option value={"__all"}>{"Select all grouping"}</Option>}
            {(apiGroupBy.length > 0 ? apiGroupBy : Object.keys(
              Object.assign({}, PublisherReportKeyColumnDescription.ColumnEnum)
            ).filter(
                (key) =>
                  key !==
                    PublisherReportKeyColumnDescription.ColumnEnum.PublisherId.toString() &&
                  key !==
                    PublisherReportKeyColumnDescription.ColumnEnum.DspId.toString() &&
                  key !==
                    PublisherReportKeyColumnDescription.ColumnEnum.Publisher.toString() &&
                  key !==
                    PublisherReportKeyColumnDescription.ColumnEnum.FourHourly.toString() &&
                  key !==
                    PublisherReportKeyColumnDescription.ColumnEnum.Daily.toString() &&
                  key !==
                    PublisherReportKeyColumnDescription.ColumnEnum.Weekly.toString() &&
                  key !==
                    PublisherReportKeyColumnDescription.ColumnEnum.Monthly.toString() &&
                  key !==
                    PublisherReportKeyColumnDescription.ColumnEnum.Yearly.toString()
              ))
              .map((key) => (
                <Option
                  title={columnsOptionDescriptionFunc(key)}
                  key={key}
                  value={key}
                >
                  {key}
                </Option>
              ))}
          </Select>
        </FormItem>

        {scheduled && (
          <div>
            <FormItem
              name="name"
              rules={[
                {
                  required: true,
                  message: "Please input scheduled report name",
                },
                {
                  max: 255,
                  message: "Name cannot be longer that 255 characters",
                },
              ]}
              label="Name"
              tooltip={"Name of scheduled report"}
            >
              <Input />
            </FormItem>

            <FormItem label="Date Time" style={{ marginBottom: 0 }}>
              <FormItem
                name="nextReport"
                rules={[
                  {
                    required: true,
                    message: "Please choose date",
                  },
                ]}
                getValueFromEvent={(val) => {
                  if (val && userTimezone) {
                    return (moment as any).tz(
                      val.format("YYYY-MM-DD HH:mm:ss"),
                      userTimezone
                    );
                  }
                  return val;
                }}
                style={{
                  display: "inline-block",
                  maxWidth: "calc(30%)",
                  minWidth: "180px",
                }}
              >
                <DatePicker
                  format="YYYY-MM-DD HH:mm:00"
                  showTime={{ format: "HH:mm" }}
                />
              </FormItem>
              <span
                style={{
                  display: "inline-block",
                  lineHeight: "32px",
                  textAlign: "left",
                  margin: "0 10px",
                }}
              >
                Frequency:
              </span>
              <FormItem
                name="frequency"
                rules={[
                  { required: true, message: "Please select report frequency" },
                ]}
                tooltip={"Each next report will be send in the same time."}
                initialValue={
                  ScheduledReportRequestReportRequestParametersObjectObject
                    .FrequencyEnum.Daily
                }
                style={{
                  display: "inline-block",
                  minWidth: "100px",
                  maxWidth: "calc(30%)",
                }}
              >
                <Select placeholder="Select frequency">
                  {Object.keys(
                    ScheduledReportRequestReportRequestParametersObjectObject.FrequencyEnum
                  ).map((period) => (
                    <Option key={period} value={period}>
                      {period}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            </FormItem>
          </div>
        )}

        <FormItem
          wrapperCol={{
            offset: 3,
            span: 21,
          }}
        >
          <Button type="primary" htmlType="submit" loading={loading}>
            {scheduled ? "Create scheduled report" : "Generate report"}
          </Button>
          <Button
            style={{
              margin: "0 0 0 1%",
            }}
            type="default"
            htmlType="button"
            onClick={() => setScheduled(!scheduled)}
          >
            {scheduled ? "Generate" : "Schedule"}
          </Button>
        </FormItem>
      </Form>

      {blobCsv && (
        <>
          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
            {hasCountryColumn && (
              <Input
                placeholder="Search Country"
                allowClear
                value={inputValue}
                onChange={handleInputChange}
                style={{ maxWidth: 240 }}
              />
            )}
            {hasSizeColumn && (
              <Input
                placeholder="Search Format Size"
                allowClear
                value={formatSizeInput}
                onChange={handleFormatSizeInputChange}
                style={{ maxWidth: 240 }}
              />
            )}
            {(data?.length ?? -1) > 0 ? (
              <Button onClick={download} icon={<DownloadOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />}>Download</Button>
            ) : null}
          </div>
          <MemoizedReportTable
            tableWidth={tableWidth}
            tableColumns={tableColumns}
            filteredSource={sortedSource}
            header={header}
            onTableChange={handleTableChange}
          />
        </>
      )}

    </div>
  );
};